import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  Alert,
  Dimensions,
  ScrollView,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ActivityIndicator,
  Avatar,
  IconButton,
  Chip,
  Searchbar,
  Menu,
  Divider,
  Badge,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchRecipes,
  deleteRecipe,
  selectRecipes,
  selectRecipesLoading,
  selectRecipesError,
} from '../store/slices/recipeSlice';
import {
  Coffee,
  Clock,
  Star,
  Eye,
  Heart,
  MoreVertical,
  Plus,
  Filter,
  Search,
  Edit3,
  Trash2,
  EyeOff,
  BookOpen,
  TrendingUp,
} from 'lucide-react-native';
import { Recipe, RecipeStatus, BrewingMethod } from '@coffeeplexity/shared';
import Toast from 'react-native-toast-message';
import { LoadingOverlay } from '../components/common/LoadingOverlay';
import { EmptyState } from '../components/common/EmptyState';
import { logger } from '../utils/logger';

type RecipeTab = 'all' | 'published' | 'drafts' | 'archived';

interface FilterOptions {
  brewingMethod?: BrewingMethod[];
  status?: RecipeStatus[];
  searchQuery?: string;
}

export function MyRecipesScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { width } = Dimensions.get('window');

  // Recipe state - use main recipes list instead of userRecipes
  const recipes = useAppSelector(selectRecipes);
  const isLoading = useAppSelector(selectRecipesLoading);
  const error = useAppSelector(selectRecipesError);

  // Local state
  const [activeTab, setActiveTab] = useState<RecipeTab>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [filterMenuVisible, setFilterMenuVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterOptions>({});
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);

  // Get current user ID from auth state
  const currentUser = useAppSelector(state => state.auth.user);
  const userId = currentUser?.id;

  // Load recipes on screen focus
  useFocusEffect(
    useCallback(() => {
      if (userId) {
        loadRecipes();
      }
    }, [activeTab, selectedFilters, userId])
  );

  const loadRecipes = async () => {
    if (!userId) {
      logger.error('No user ID available');
      return;
    }

    try {
      setRefreshing(true);
      // Use fetchRecipes with creator_id filter to get user's own recipes
      await dispatch(
        fetchRecipes({
          page: 1,
          filters: {
            creator_id: userId,
          },
        })
      ).unwrap();
    } catch (error: any) {
      logger.error('Failed to load recipes:', error);
      Toast.show({
        type: 'error',
        text1: 'Failed to load recipes',
        text2: error?.message || 'Please check your connection and try again',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadRecipes();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSelectedFilters(prev => ({ ...prev, searchQuery: query }));
  };

  const handleFilterChange = (filters: Partial<FilterOptions>) => {
    setSelectedFilters(prev => ({ ...prev, ...filters }));
  };

  const clearFilters = () => {
    setSelectedFilters({});
    setSearchQuery('');
  };

  const filteredRecipes = useMemo((): Recipe[] => {
    let filtered = recipes || [];

    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(recipe => recipe.status === activeTab);
    }

    // Filter by search query
    if (selectedFilters.searchQuery) {
      const query = selectedFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        recipe =>
          recipe.title.toLowerCase().includes(query) ||
          recipe.description.toLowerCase().includes(query) ||
          recipe.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Filter by brewing method
    if (
      selectedFilters.brewingMethod &&
      selectedFilters.brewingMethod.length > 0
    ) {
      filtered = filtered.filter(recipe =>
        selectedFilters.brewingMethod!.includes(recipe.brewing_method)
      );
    }

    // Filter by status
    if (selectedFilters.status && selectedFilters.status.length > 0) {
      filtered = filtered.filter(recipe =>
        selectedFilters.status!.includes(recipe.status)
      );
    }

    return filtered;
  }, [recipes, activeTab, selectedFilters]);

  const handleRecipeAction = (
    recipe: Recipe,
    action: 'edit' | 'delete' | 'view'
  ) => {
    setSelectedRecipe(recipe);
    setActionMenuVisible(false);

    switch (action) {
      case 'edit':
        // Navigate to createRecipe screen with recipeId for editing
        navigation.navigate(
          'createRecipe' as never,
          { recipeId: recipe.id } as never
        );
        break;
      case 'delete':
        handleDeleteRecipe(recipe);
        break;
      case 'view':
        // Navigate to recipeDetail within the profile stack for proper back navigation
        navigation.navigate(
          'recipeDetail' as never,
          { recipeId: recipe.id } as never
        );
        break;
    }
  };

  const handleDeleteRecipe = (recipe: Recipe) => {
    Alert.alert(
      'Delete Recipe',
      `Are you sure you want to delete "${recipe.title}"? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await dispatch(deleteRecipe(recipe.id)).unwrap();
              Toast.show({
                type: 'success',
                text1: 'Recipe deleted successfully',
              });
            } catch (error: any) {
              logger.error('Failed to delete recipe:', error);
              Toast.show({
                type: 'error',
                text1: 'Failed to delete recipe',
                text2:
                  error?.message ||
                  'Please check your connection and try again',
              });
            }
          },
        },
      ]
    );
  };

  const getStatusColor = (status: RecipeStatus) => {
    switch (status) {
      case 'published':
        return theme.colors.primary;
      case 'draft':
        return theme.colors.secondary;
      case 'archived':
        return theme.colors.outline;
      case 'pending_review':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const getStatusLabel = (status: RecipeStatus) => {
    switch (status) {
      case 'published':
        return 'Published';
      case 'draft':
        return 'Draft';
      case 'archived':
        return 'Archived';
      case 'pending_review':
        return 'Pending Review';
      default:
        return status;
    }
  };

  const getBrewingMethodIcon = (method: BrewingMethod) => {
    // You can add specific icons for each brewing method
    return Coffee;
  };

  const renderRecipeCard = useCallback(
    ({ item: recipe }: { item: Recipe }) => (
      <Card
        style={[styles.recipeCard, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content style={styles.recipeCardContent}>
          {/* Recipe Header */}
          <View style={styles.recipeHeader}>
            <View style={styles.recipeTitleContainer}>
              <Text
                variant='titleMedium'
                style={styles.recipeTitle}
                numberOfLines={2}
              >
                {recipe.title}
              </Text>
              <Chip
                mode='outlined'
                textStyle={{ fontSize: 10 }}
                style={[
                  styles.statusChip,
                  { borderColor: getStatusColor(recipe.status) },
                ]}
                textStyle={{ color: getStatusColor(recipe.status) }}
              >
                {getStatusLabel(recipe.status)}
              </Chip>
            </View>

            <Menu
              visible={actionMenuVisible && selectedRecipe?.id === recipe.id}
              onDismiss={() => setActionMenuVisible(false)}
              anchor={
                <IconButton
                  icon={MoreVertical}
                  size={20}
                  onPress={() => {
                    setSelectedRecipe(recipe);
                    setActionMenuVisible(true);
                  }}
                />
              }
            >
              <Menu.Item
                onPress={() => handleRecipeAction(recipe, 'view')}
                title='View Recipe'
                leadingIcon='eye'
              />
              <Menu.Item
                onPress={() => handleRecipeAction(recipe, 'edit')}
                title='Edit Recipe'
                leadingIcon='pencil'
              />
              <Divider />
              <Menu.Item
                onPress={() => handleRecipeAction(recipe, 'delete')}
                title='Delete Recipe'
                leadingIcon='delete'
                titleStyle={{ color: theme.colors.error }}
              />
            </Menu>
          </View>

          {/* Recipe Description */}
          <Text
            variant='bodyMedium'
            style={styles.recipeDescription}
            numberOfLines={2}
          >
            {recipe.description}
          </Text>

          {/* Recipe Stats */}
          <View style={styles.recipeStats}>
            <View style={styles.statItem}>
              <Clock size={16} color={theme.colors.outline} />
              <Text variant='bodySmall' style={styles.statText}>
                {recipe.total_time_minutes}m
              </Text>
            </View>

            <View style={styles.statItem}>
              <Star size={16} color={theme.colors.outline} />
              <Text variant='bodySmall' style={styles.statText}>
                {recipe.rating?.toFixed(1) || 'N/A'}
              </Text>
            </View>

            <View style={styles.statItem}>
              <Eye size={16} color={theme.colors.outline} />
              <Text variant='bodySmall' style={styles.statText}>
                {recipe.view_count}
              </Text>
            </View>

            <View style={styles.statItem}>
              <Heart size={16} color={theme.colors.outline} />
              <Text variant='bodySmall' style={styles.statText}>
                {recipe.favorite_count}
              </Text>
            </View>
          </View>

          {/* Recipe Tags */}
          <View style={styles.recipeTags}>
            <Chip
              mode='outlined'
              icon={() => <Coffee size={14} />}
              style={styles.methodChip}
            >
              {recipe.brewing_method.replace(/_/g, ' ')}
            </Chip>

            {recipe.tags.slice(0, 2).map((tag, index) => (
              <Chip key={index} mode='outlined' style={styles.tagChip}>
                {tag}
              </Chip>
            ))}

            {recipe.tags.length > 2 && (
              <Chip mode='outlined' style={styles.tagChip}>
                +{recipe.tags.length - 2}
              </Chip>
            )}
          </View>

          {/* Recipe Actions */}
          <View style={styles.recipeActions}>
            <Button
              mode='outlined'
              onPress={() => handleRecipeAction(recipe, 'view')}
              style={styles.actionButton}
              compact
            >
              View
            </Button>

            <Button
              mode='contained'
              onPress={() => handleRecipeAction(recipe, 'edit')}
              style={styles.actionButton}
              compact
            >
              Edit
            </Button>
          </View>
        </Card.Content>
      </Card>
    ),
    [
      theme,
      actionMenuVisible,
      selectedRecipe,
      getStatusColor,
      getStatusLabel,
      handleRecipeAction,
    ]
  );

  const renderEmptyState = () => (
    <EmptyState
      icon={BookOpen}
      title='No Recipes Yet'
      description={
        activeTab === 'all'
          ? "You haven't created any recipes yet. Start sharing your coffee expertise!"
          : `No ${activeTab} recipes found.`
      }
      action={
        <Button
          mode='contained'
          onPress={() => navigation.navigate('createRecipe' as never)}
          icon={Plus}
        >
          Create Your First Recipe
        </Button>
      }
    />
  );

  const renderTabBar = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={[styles.tabBar, { backgroundColor: theme.colors.surface }]}
      contentContainerStyle={styles.tabBarContent}
    >
      {(['all', 'published', 'drafts', 'archived'] as RecipeTab[]).map(tab => (
        <Button
          key={tab}
          mode={activeTab === tab ? 'contained' : 'outlined'}
          onPress={() => setActiveTab(tab)}
          style={styles.tabButton}
          contentStyle={styles.tabButtonContent}
          compact
        >
          <View style={styles.tabButtonInner}>
            <Text
              style={[
                styles.tabButtonText,
                {
                  color:
                    activeTab === tab
                      ? theme.colors.onPrimary
                      : theme.colors.primary,
                },
              ]}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Text>
            {tab !== 'all' && (
              <View
                style={[
                  styles.customBadge,
                  {
                    backgroundColor:
                      activeTab === tab
                        ? theme.colors.onPrimary
                        : theme.colors.primary,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.badgeText,
                    {
                      color:
                        activeTab === tab
                          ? theme.colors.primary
                          : theme.colors.onPrimary,
                    },
                  ]}
                >
                  {recipes?.filter(r => r.status === tab).length || 0}
                </Text>
              </View>
            )}
          </View>
        </Button>
      ))}
    </ScrollView>
  );

  if (isLoading && !refreshing) {
    return <LoadingOverlay />;
  }

  // filteredRecipes is now memoized above

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Header with Search and Filters */}
      <View style={[styles.header, { backgroundColor: theme.colors.surface }]}>
        <View style={styles.headerTop}>
          <Text variant='headlineSmall' style={styles.headerTitle}>
            My Recipes
          </Text>

          <View style={styles.headerActions}>
            <IconButton
              icon={Search}
              size={24}
              onPress={() => setShowSearch(!showSearch)}
              iconColor={theme.colors.onSurface}
            />

            <Menu
              visible={filterMenuVisible}
              onDismiss={() => setFilterMenuVisible(false)}
              anchor={
                <IconButton
                  icon={Filter}
                  size={24}
                  onPress={() => setFilterMenuVisible(true)}
                  iconColor={theme.colors.onSurface}
                />
              }
            >
              <Menu.Item
                onPress={() => {
                  setFilterMenuVisible(false);
                  // Add filter logic here
                }}
                title='Brewing Method'
                leadingIcon='coffee'
              />
              <Menu.Item
                onPress={() => {
                  setFilterMenuVisible(false);
                  // Add filter logic here
                }}
                title='Difficulty'
                leadingIcon='trending-up'
              />
              <Menu.Item
                onPress={() => {
                  setFilterMenuVisible(false);
                  clearFilters();
                }}
                title='Clear Filters'
                leadingIcon='close'
              />
            </Menu>
          </View>
        </View>

        {/* Search Bar */}
        {showSearch && (
          <Searchbar
            placeholder='Search your recipes...'
            onChangeText={handleSearch}
            value={searchQuery}
            style={styles.searchBar}
            onClearIconPress={() => {
              setSearchQuery('');
              setSelectedFilters(prev => ({ ...prev, searchQuery: undefined }));
            }}
          />
        )}

        {/* Active Filters */}
        {Object.keys(selectedFilters).length > 0 && (
          <View style={styles.activeFilters}>
            <Text variant='bodySmall' style={styles.filtersLabel}>
              Active filters:
            </Text>
            <Button
              mode='text'
              onPress={clearFilters}
              compact
              textColor={theme.colors.primary}
            >
              Clear All
            </Button>
          </View>
        )}
      </View>

      {/* Tab Bar */}
      {renderTabBar()}

      {/* Recipes List */}
      <FlatList
        data={filteredRecipes}
        renderItem={renderRecipeCard}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.recipesList}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        windowSize={10}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
        ListEmptyComponent={renderEmptyState}
        numColumns={width < 768 ? 1 : 2}
        columnWrapperStyle={width >= 768 ? styles.recipeRow : undefined}
      />
    </SafeAreaView>
  );
}

// Add Surface import at the top if not already present

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    elevation: 2,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  headerTitle: {
    fontWeight: '600',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchBar: {
    marginBottom: 8,
    elevation: 0,
  },
  activeFilters: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  filtersLabel: {
    opacity: 0.7,
  },
  tabBar: {
    paddingVertical: 4,
    elevation: 1,
    maxHeight: 56,
  },
  tabBarContent: {
    paddingHorizontal: 16,
    gap: 8,
    alignItems: 'center',
  },
  tabButton: {
    minWidth: 100,
    marginHorizontal: 0,
    height: 40,
  },
  tabButtonContent: {
    paddingHorizontal: 12,
    height: 40,
  },
  tabButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  customBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
  },
  recipesList: {
    padding: 16,
    paddingBottom: 16,
  },
  recipeRow: {
    justifyContent: 'space-between',
  },
  recipeCard: {
    marginBottom: 16,
    elevation: 2,
    flex: 1,
    marginHorizontal: 4,
  },
  recipeCardContent: {
    padding: 16,
  },
  recipeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  recipeTitleContainer: {
    flex: 1,
    marginRight: 8,
  },
  recipeTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  statusChip: {
    alignSelf: 'flex-start',
  },
  recipeDescription: {
    opacity: 0.7,
    marginBottom: 12,
    lineHeight: 20,
  },
  recipeStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
    alignItems: 'center',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    opacity: 0.7,
  },
  recipeTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  methodChip: {
    backgroundColor: '#f0f0f0',
  },
  tagChip: {
    backgroundColor: '#f8f8f8',
  },
  recipeActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
  },
});
